/**
 * Slot ownership: whose half of a report a slot belongs to (DERP-247).
 *
 * A report has two halves — the crew's and the client's — and one flag decides
 * which a slot is in. This file is the only place that flag is declared, named
 * or read, because it is asserted in a lot of places otherwise: ten element
 * config shapes, five preview renderers, two table paths and the jobs fill
 * flow. Ten hand-written copies of `client_fillable?: boolean` is ten places to
 * forget when the eleventh fillable element type arrives.
 *
 * Its own file rather than `./types` because `types` already imports from
 * `./table-types`, and both of them need this — a shared leaf keeps that a tree
 * instead of a cycle.
 */

import type {ElementType} from './types';

/**
 * Which half of a report is in question — a slot's, or a reader's.
 *
 * A slot belongs to one half (`client_fillable`); a surface is being looked at
 * by one half (`FillSurface.audience`). Same axis, so one type names both, and
 * "is this slot mine?" is a comparison rather than a special case.
 *
 * Lives here rather than in `jobs` because the preview needs it and sits BELOW
 * jobs — the same direction `isClientFillable` had to move for the same reason.
 * Mirrors the API's `missing_for(audience=…)`.
 */
export type FillAudience = 'crew' | 'client';

/**
 * The ownership flag, mixed into every element config that can carry one —
 * standalone (`./types`) and in-cell (`./table-types`) alike.
 *
 * A client-owned slot is left out of the crew's required-field count, is locked
 * on the crew's canvas, and is the only kind a public share link offers a
 * visitor. So this is an ownership switch, not a visibility one.
 *
 * Absent means the crew's, and it is written back as `undefined` rather than
 * `false` (see `FilledByToggle`), so a template that never touched the switch
 * carries no key at all and round-trips unchanged.
 *
 * A type alias, not an interface, deliberately: an interface has no implicit
 * index signature, so `(patch: ClientFillableConfig) => void` would reject the
 * `(patch: Record<string, unknown>) => void` every config-patch callback in the
 * toolbar actually is.
 */
export type ClientFillableConfig = {
  client_fillable?: boolean;
};

/**
 * The standalone element types that can carry a fill answer, and so can be set
 * aside for the client.
 *
 * An ALLOWLIST, unlike `NO_REQUIRED_TYPES` and the other per-type lists in
 * `ItemEditor`: five of the thirteen element types can be answered at all, so
 * naming the five is both shorter and the honest shape. Writing it as a
 * denylist over `NO_REQUIRED_TYPES` looks tidier and is wrong — `group`,
 * `page_break` and `page_number` are in neither list, and would silently start
 * offering an ownership switch for an answer they cannot hold.
 *
 * `table` is absent because its slots are its cells, and each cell element
 * carries its own flag. Cell types are a different (larger) set — they include
 * `date_time`, which has no standalone element — so they are not derived from
 * here; see the API's `REQUIRABLE_REQUIREMENT_TYPES` for that half.
 */
export const CLIENT_OWNABLE_ELEMENT_TYPES: readonly ElementType[] = [
  'text',
  'photo',
  'checkbox',
  'dropdown',
  'signature',
];

/**
 * Whether a config declares its slot the client's to fill.
 *
 * Takes `unknown` because the callers are split between typed element configs
 * and raw stored JSON, and a slot that carries no config at all (`heading` is
 * decorative and has neither this flag nor `required`) has to answer `false`
 * rather than throw.
 *
 * Mirrors the API's `photo_report_templates._is_client_owned`. The two decide
 * the same question about the same stored bytes, so they must agree exactly —
 * the server is the authority, and a page that disagreed would either offer a
 * slot the server rejects or lock one it expects answered.
 */
export function isClientFillable(config: unknown): boolean {
  return (
    typeof config === 'object' &&
    config !== null &&
    (config as ClientFillableConfig).client_fillable === true
  );
}
